import { del_, get, get_, post, post_, put_ } from './base'

import type {
  BalanceInfo,
  BillReportUserConfig,
  Invoice,
  InvoiceHistory,
  InvoiceReception,
  InvoiceTitle,
  OrderRequest,
} from '@/types/bill'

export const fetchAccountWallet = (
  url: string,
  params: Record<string, string>,
) => {
  return get('account/wallet', { params })
}

export const fetchAccountProfile = (
  url: string,
  params: Record<string, string>,
) => {
  return get('account/profile', { params })
}

export const recharge = (amount: number) => {
  return post('account/recharge', { body: { amount } })
}

const billApiPrefix = '/api/sa/bill'
const BILL_API_PREFIX_V1 = '/api/sa/bill/api/v1'

/**
 *
 * @param url mock发票数据
 * @param params
 * @returns mock发票数据
 */
export const getMockInvoiceList = async (payload: any) => {
  const res = await post_(
    `${billApiPrefix}/api/v1/invoice/invoices/mock-create`,
    payload,
  )
  return res
}

export async function getPrice(request: OrderRequest) {
  const res = await post_(`${BILL_API_PREFIX_V1}/get_price`, request)
  return res
}

// 查询价格 /price/query
type ProductType = 'gpu' | 'vdisk' | 'disk' | 'ecs' | 'baremetal' | 'harbor' | 'model'

type QueryPriceRequest = {
  productType: ProductType
  clusterIds?: string[]
  productSpecIds?: string[]
  publishStatus?: boolean
  billingEnabled?: boolean
}
export async function getQueryPrice(request: QueryPriceRequest) {
  // 如果 billing 未启用，则不发起请求，直接返回空结果
  if (request.billingEnabled === false) {
    return Promise.resolve({ priceItems: [] })
  }
  // 从请求中移除 billingEnabled，因为它不应该发送到服务器
  const { billingEnabled: _, ...requestBody } = request
  const res = await post_(`${BILL_API_PREFIX_V1}/price/query`, requestBody)
  return res
}

export async function makeOrder(request: OrderRequest) {
  const res = await post_(`${billApiPrefix}/api/v1/order`, request)
  return res
}

export async function getInvoiceList(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(`${billApiPrefix}/api/v1/orders/list?${params}`)
  return res
}

export async function getInvoiceDetail(id: string) {
  const res = await get_(`${billApiPrefix}/api/v1/orders/${id}/detail`)
  return res
}

export async function payWithBalance(id: string) {
  const res = await post_(`${billApiPrefix}/api/v1/orders/${id}/pay`)
  return res
}

export async function tradePagePay(id: string, payload: any) {
  const res = await post_(
    `${billApiPrefix}/api/v1/orders/${id}/tradePagePay`,
    payload,
  )
  return res
}

export async function cancelOrder(id: string) {
  const res = await post_(`${billApiPrefix}/api/v1/orders/${id}/cancel`)
  return res
}
/**
 * 获取订单项列表
 * @returns 订单项列表
 */
export async function getOrderItems(id: string) {
  const res = await get_(`${billApiPrefix}/api/v1/orders/${id}/items`)
  return res
}

export async function billsRecharge(
  payload: any,
): Promise<{ order_id: string }> {
  const res = await post_(
    `${billApiPrefix}/api/v1/orders/rechargeBalance`,
    payload,
  )
  return res
}
/**
 * 获取余额信息
 * @returns 余额信息
 */
export async function getBalanceInfo() {
  const res = await get_(`${billApiPrefix}/api/v1/balance/info`)
  return res
}

/**
 * 获取可开票金额
 * @returns 可开票金额
 */
export async function getAllInvoicesAmount() {
  const res = await get_(`${billApiPrefix}/api/v1/invoice/invoices/all-amount`)
  return res
}

/**
 * 获取月度收支总览
 * @returns 月度收支总览
 */
export async function getMonthlyBillsOverview(payload: any) {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/month-bills/overview?${params}`,
  )
  return res
}

/**
 * 获取订单出账明细
 * @param payload
 * @returns 订单出账明细
 */

export async function getOrderBillsList(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(`${billApiPrefix}/api/v1/bills/orders?${params}`)
  return res
}

/**
 * 获取账户列表
/**
 * 获取账户列表
 * @returns 账户列表
 */
export async function getAccountList(payload: any) {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(`${billApiPrefix}/api/v1/user/users?${params}`)
  return res
}

/**
 * 获取账单历史
 * @returns 账单历史
 */
export async function getBillsHistory(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(`${billApiPrefix}/api/v1/month-bills?${params}`)
  return res
}

/**
 * 导出产品账单
 * @param payload 
 * @returns 产品账单数据
 */
export async function exportProductsBills(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const url = `${BILL_API_PREFIX_V1}/bills/export?${params}`
  // 获取认证 token
  const accessToken = typeof window !== 'undefined' ? localStorage.getItem('console_token') || '' : ''
    
  const headers: Record<string, string> = {}
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }
  headers['Access-Control-Expose-Headers'] = 'Content-Disposition'
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers,
  })

  if (!response.ok) {
    throw new Error('Network response was not ok')
  }

  // 获取文件名
  // const contentDisposition = response.headers.get('Content-Disposition')
  let filename = `账单_${payload.yearMonth || 'export'}.xlsx`

  // 获取 blob 数据
  const blob = await response.blob()

  return { blob, filename }
}
/**
 * 按照产品维度获取账单
 * @param payload 查询参数，如果包含 export: true 则返回blob数据用于下载
 * @returns 产品账单数据（当export为true时返回{ blob: Blob, filename: string }）
 */
export async function getProductsBills(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  
  const url = `${BILL_API_PREFIX_V1}/bills/products?${params}`
  
  // 如果是导出请求，返回blob数据和文件名
  if (payload?.export === true) {
    // 获取认证 token
    const accessToken = typeof window !== 'undefined' ? localStorage.getItem('console_token') || '' : ''
    
    const headers: Record<string, string> = {}
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    }
    headers['Access-Control-Expose-Headers'] = 'Content-Disposition'
    
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers,
    })

    if (!response.ok) {
      throw new Error('Network response was not ok')
    }

    // 获取文件名
    // const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `账单_${payload.yearMonth || 'export'}.xlsx`
    
    // if (contentDisposition) {
    //   // 优先处理 RFC 5987 格式: filename*=UTF-8''encoded-filename
    //   const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''(.+)/i)
    //   if (rfc5987Match) {
    //     try {
    //       console.log(rfc5987Match[1])
    //       filename = decodeURIComponent(rfc5987Match[1])
    //     } catch (e) {
    //       // 如果解码失败，尝试其他方式
    //     }
    //   } else {
    //     // 处理标准格式: filename="..." 或 filename=...
    //     const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    //     console.log(filenameMatch)
    //     if (filenameMatch && filenameMatch[1]) {
    //       filename = filenameMatch[1].replace(/['"]/g, '')
    //       // 处理 URL 编码的文件名
    //       try {
    //         filename = decodeURIComponent(filename)
    //       } catch (e) {
    //         // 如果解码失败，保持原样
    //       }
    //     }
    //   }
    // }

    // 获取 blob 数据
    const blob = await response.blob()

    return { blob, filename }
  }
  
  // 普通查询请求，返回JSON数据
  const res = await get_(url)
  return res
}

/**
 * 成本明细
 * @param payload
 * @returns 产品账单
 */
export async function getCostReportBills(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${BILL_API_PREFIX_V1}/consumptions/month-list?${params}`,
  )
  return res
}

/**
 * 获取月消费各个维度的汇总
 * @param payload
 * @returns 产品账单
 */
export async function getCostReportSummary(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${BILL_API_PREFIX_V1}/consumptions/month-summary?${params}`,
  )
  return res
}

/**
 * 获取充值历史
 */
/**
 * 获取充值历史
 * @returns 充值历史
 */
export async function getRechargeHistory(payload: any) {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/orders/list-recharges?${params}`,
  )
  return res
}

export async function getFundList(
  limit: number,
  offset: number,
  payload?: any,
) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/payments/list?limit=${limit}&offset=${offset}&${params}`,
  )
  return res
}

/**
 * 获取月度成本趋势
 * @param payload
 * @returns
 */
export async function getMonthlyCostTrend(payload: any) {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/consumptions/month-overview?${params}`,
  )
  return res
}

export async function getCycleBills(payload: any) {
  const res = await post_(`${billApiPrefix}/api/v1/payments/summary`, payload)
  return res
}

export async function getGroupedSummary(
  payload: any,
): Promise<{ id: string; name: string; amount: number }[]> {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res: { id: string; amount: number; name: string }[] = await get_(
    `${billApiPrefix}/api/v1/bills/getSummary?${params}`,
  )
  return res
}

export async function getCost(payload: any) {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(`${billApiPrefix}/api/v1/bills/getCost?${params}`)
  return res
}

export async function getUserList(payload: any) {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(`/api/v1/users?${params}`)
  return res
}

export async function getUser(userId: string) {
  const res = await get_(`/api/v1/users/${userId.replace(/-/g, '')}`)
  return res
}

export async function getProject(projectId: string) {
  const res = await get_(`/api/v1/projects/${projectId.replace(/-/g, '')}`)
  return res
}

export async function getProjectList() {
  const res = await get_(`/api/v1/projects`)
  return res
}

export async function getUserBalanceByAdmin(userId: string) {
  const res = await get_(
    `${billApiPrefix}/api/v1/admin/balance/userInfo?userId=${userId}`,
  )
  return res
}

export async function submitAdminRecharge(payload: any) {
  const res = await post_(
    `${billApiPrefix}/api/v1/admin/balance/recharge`,
    payload,
  )
  return res
}

export async function getBillListByAdmin(payload: any) {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(`${billApiPrefix}/api/v1/admin/orders/list?${params}`)
  return res
}
/**
 * 获取产品列表（支持分页和搜索）
 * @param limit - 每页数量
 * @param offset - 页码（从1开始）
 * @param search - 产品名称搜索
 * @param filters - 筛选条件
 * @returns 包含产品列表的响应数据
 */
export async function fetchPlans(
  limit: number,
  offset: number,
  search?: string,
  filters?: {
    regions?: string[]
    productTypes?: string[]
    clusterIds?: string[]
  },
) {
  const params = new URLSearchParams()
  params.append('limit', limit.toString())
  params.append('offset', offset.toString())
  if (search) {
    params.append('search', search)
  }
  if (filters?.regions && filters.regions.length > 0) {
    params.append('regions', filters.regions.join(','))
  }
  if (filters?.productTypes && filters.productTypes.length > 0) {
    params.append('productTypes', filters.productTypes.join(','))
  }
  if (filters?.clusterIds && filters.clusterIds.length > 0) {
    params.append('clusterIds', filters.clusterIds.join(','))
  }
  return await get_(
    `${billApiPrefix}/api/v1/admin/plan/plans?${params.toString()}`,
  )
}

export async function createPlan(params: any) {
  return await post_(`${billApiPrefix}/api/v1/admin/plan/plans`, params)
}

export async function deletePlan(id: string) {
  return await del_(`${billApiPrefix}/api/v1/admin/plan/plans/${id}`)
}

export async function updatePlan(id: string, params: Record<string, any>) {
  return await put_(`${billApiPrefix}/api/v1/admin/plan/plans/${id}`, params)
}

export async function getPlan(id: string) {
  return await get_(`${billApiPrefix}/api/v1/admin/plan/plans/${id}`)
}

export async function setOrderPriceByAdmin(orderId: string, payload: any) {
  return await post_(
    `${billApiPrefix}/api/v1/admin/orders/${orderId}/setPrice`,
    payload,
  )
}

export async function getMonthlyCostBills(payload: any) {
  const res = await post_(`${billApiPrefix}/api/v1/payments/summary`, payload)
  return res
}

export async function getGroupedCostSummary(
  payload: any,
): Promise<{ id: string; name: string; amount: number }[]> {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/consumptions/getSummary?${params}`,
  )
  return res
}

export async function getConsumptionCost(payload: any) {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/consumptions/getCost?${params}`,
  )
  return res
}

export async function getReportUserConfig(): Promise<BillReportUserConfig> {
  const res = await get_(`${billApiPrefix}/api/v1/user/config/get`)
  return res
}

export async function createOrUpdateReportUserConfig(
  payload: BillReportUserConfig,
) {
  const res = await post_(
    `${billApiPrefix}/api/v1/user/config/createOrUpdate`,
    payload,
  )
  return res
}

export async function setUserBalanceQuotaByAdmin(payload: any) {
  const res = await post_(
    `${billApiPrefix}/api/v1/admin/balance/modifyQuota`,
    payload,
  )
  return res
}

export async function getUserBalanceList(
  limit: number,
  offset: number,
): Promise<{ results: BalanceInfo[]; total: number }> {
  const res = await get_(
    `${billApiPrefix}/api/v1/admin/balance/list?limit=${limit}&offset=${offset}`,
  )
  return res
}

/**
 * 获取管理员促销活动列表
 * @param page - 每页显示的记录数量，用于分页控制
 * @param pageSize - 偏移量，表示从第几条记录开始获取，用于分页控制
 * @param serach - 搜索条件
 * @param planId - 产品ID
 * @returns 包含管理员促销活动列表的响应数据
 */
export async function getAdminPromotionList(
  page: number,
  pageSize: number,
  search: string,
  planId?: string,
) {
  const params = new URLSearchParams()
  params.append('page', page.toString())
  params.append('pageSize', pageSize.toString())
  if (search) {
    params.append('search', search)
  }
  if (planId) {
    params.append('planId', planId)
  }
  // 调用 get_ 函数发起 GET 请求，获取指定分页的管理员促销活动列表
  const res = await get_(
    `${billApiPrefix}/api/v1/admin/promotion/promotions?${params.toString()}`,
  )
  // 返回请求响应数据
  return res
}

/**
 * 创建管理员促销活动
 * @param payload - 创建促销活动所需的参数对象，包含促销活动的相关信息
 * @returns 包含创建促销活动请求响应的对象
 */
export async function postAdminPromotion(payload: any) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以创建管理员促销活动
  const res = await post_(
    `${billApiPrefix}/api/v1/admin/promotion/promotions`,
    payload,
  )
  // 返回请求响应数据
  return res
}

/**
 * 获取管理员促销活动详情
 * @param id - 促销活动 id
 * @returns 包含管理员促销活动的响应数据
 */
export async function getAdminPromotionDetail(id: string) {
  // 调用 get_ 函数发起 GET 请求，获取指定的管理员促销活动
  const res = await get_(
    `${billApiPrefix}/api/v1/admin/promotion/promotions/${id}`,
  )
  // 返回请求响应数据
  return res
}

/**
 * 获取管理员促销活动列表
 * @param page - 每页显示的记录数量，用于分页控制
 * @param pageSize - 偏移量，表示从第几条记录开始获取，用于分页控制
 * @returns 包含管理员促销活动列表的响应数据
 */
export async function getAdminPromotionDetailProducts(
  page: number,
  pageSize: number,
  id: string,
) {
  // 调用 get_ 函数发起 GET 请求，获取指定分页的管理员促销活动列表
  const res = await get_(
    `${billApiPrefix}/api/v1/admin/promotion/promotions/${id}/plans/active?page=${page}&pageSize=${pageSize}&id=${id}`,
  )
  // 返回请求响应数据
  return res
}

/**
 * 停止促销活动
 * @param id - 促销活动 id
 * @returns 包含停止促销活动请求响应的对象
 */
export async function stopAdminPromotion(id: string) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以停止管理员促销活动
  const res = await post_(
    `${billApiPrefix}/api/v1/admin/promotion/promotions/${id}/stop`,
    {},
  )
  // 返回请求响应数据
  return res
}

/**
 * 停止促销活动
 * @param id - 促销活动 id
 * @returns 包含停止促销活动请求响应的对象
 */
export async function stopAdminPromotionProduct(id: string, planIds: string[]) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以停止管理员促销活动
  const res = await post_(
    `${billApiPrefix}/api/v1/admin/promotion/promotions/${id}/plans/batch-stop`,
    {
      planIds,
    },
  )
  // 返回请求响应数据
  return res
}

export async function getInvoiceTitleList(
  page: number,
  pageSize: number,
): Promise<{ results: InvoiceTitle[]; total: number }> {
  const res = await get_(
    `${billApiPrefix}/api/v1/invoice/titles?page=${page}&pageSize=${pageSize}`,
  )
  return res
}

export async function postInvoiceTitle(payload: any) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以创建管理员促销活动
  const res = await post_(`${billApiPrefix}/api/v1/invoice/titles`, payload)
  // 返回请求响应数据
  return res
}

export async function getInvoiceTitleDetail(id: string) {
  // 调用 get_ 函数发起 GET 请求，获取指定分页的管理员促销活动列表
  const res = await get_(`${billApiPrefix}/api/v1/invoice/titles/${id}`)
  // 返回请求响应数据
  return res
}

// 发票明细接口
export async function getInvoiceDetailList(id: string, payload: any) {
  const params = Object.keys(payload || {})
    .filter(key => payload[key] !== undefined && payload[key] !== null)
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  
  const url = `${billApiPrefix}/api/v1/invoice/histories/${id}/details?${params}`
  
  // 如果是导出请求，返回blob数据和文件名
  if (payload?.export === true) {
    // 获取认证 token
    const accessToken = typeof window !== 'undefined' ? localStorage.getItem('console_token') || '' : ''
    
    const headers: Record<string, string> = {}
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    }
    headers['Access-Control-Expose-Headers'] = 'Content-Disposition'
    
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers,
    })

    if (!response.ok) {
      throw new Error('Network response was not ok')
    }

    // 获取文件名
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `发票明细_${id || 'export'}.xlsx`
    
    if (contentDisposition) {
      // 优先处理 RFC 5987 格式: filename*=UTF-8''encoded-filename
      const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''(.+)/i)
      if (rfc5987Match) {
        try {
          filename = decodeURIComponent(rfc5987Match[1])
        } catch (e) {
          // 如果解码失败，尝试其他方式
        }
      } else {
        // 处理标准格式: filename="..." 或 filename=...
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
          // 处理 URL 编码的文件名
          try {
            filename = decodeURIComponent(filename)
          } catch (e) {
            // 如果解码失败，保持原样
          }
        }
      }
    }

    // 获取 blob 数据
    const blob = await response.blob()

    return { blob, filename }
  }
  
  // 普通查询请求，返回JSON数据
  const res = await get_(url)
  return res
}

export async function putInvoiceTitle(id: string, payload: any) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以创建管理员促销活动
  const res = await put_(
    `${billApiPrefix}/api/v1/invoice/titles/${id}`,
    payload,
  )
  // 返回请求响应数据
  return res
}

export async function deleteInvoiceTitle(id: string) {
  // 调用 get_ 函数发起 GET 请求，获取指定分页的管理员促销活动列表
  const res = await del_(`${billApiPrefix}/api/v1/invoice/titles/${id}`)
  // 返回请求响应数据
  return res
}

export async function getInvoiceReceptionList(
  page: number,
  pageSize: number,
): Promise<{ results: InvoiceReception[]; total: number }> {
  const res = await get_(
    `${billApiPrefix}/api/v1/invoice/receptions?page=${page}&pageSize=${pageSize}`,
  )
  return res
}

export async function postInvoiceReception(payload: any) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以创建管理员促销活动
  const res = await post_(`${billApiPrefix}/api/v1/invoice/receptions`, payload)
  // 返回请求响应数据
  return res
}

export async function getInvoiceReceptionDetail(id: string) {
  // 调用 get_ 函数发起 GET 请求，获取指定分页的管理员促销活动列表
  const res = await get_(`${billApiPrefix}/api/v1/invoice/receptions/${id}`)
  // 返回请求响应数据
  return res
}

export async function putInvoiceReception(id: string, payload: any) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以创建管理员促销活动
  const res = await put_(
    `${billApiPrefix}/api/v1/invoice/receptions/${id}`,
    payload,
  )
  // 返回请求响应数据
  return res
}

export async function deleteInvoiceReception(id: string) {
  // 调用 get_ 函数发起 GET 请求，获取指定分页的管理员促销活动列表
  const res = await del_(`${billApiPrefix}/api/v1/invoice/receptions/${id}`)
  // 返回请求响应数据
  return res
}

export async function getApplyInvoiceList(
  page: number,
  pageSize: number,
  status?: string,
): Promise<{ results: Invoice[]; total: number }> {
  const res = await get_(
    `${billApiPrefix}/api/v1/invoice/invoices?page=${page}&pageSize=${pageSize}&status=${status}`,
  )
  return res
}

export async function applyInvoice(payload: any) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以创建管理员促销活动
  const res = await post_(
    `${billApiPrefix}/api/v1/invoice/invoices/apply`,
    payload,
  )
  // 返回请求响应数据
  return res
}

export async function amountInvoice(payload: any) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以创建管理员促销活动
  const res = await post_(
    `${billApiPrefix}/api/v1/invoice/invoices/get-amount`,
    payload,
  )
  // 返回请求响应数据
  return res
}

export async function getInvoiceHistoriesList(
  page: number,
  pageSize: number,
): Promise<{ results: InvoiceHistory[]; total: number }> {
  const res = await get_(
    `${billApiPrefix}/api/v1/invoice/histories/list?page=${page}&pageSize=${pageSize}`,
  )
  return res
}

export async function getInvoiceHistoryDetail(id: string) {
  // 调用 get_ 函数发起 GET 请求，获取指定分页的管理员促销活动列表
  const res = await get_(
    `${billApiPrefix}/api/v1/invoice/histories/detail/${id}`,
  )
  // 返回请求响应数据
  return res
}

export async function getAdminInvoiceHistoriesList(params: {
  page: number
  pageSize: number
  category: string
  status: string
  invoiceType: string
}): Promise<{ results: InvoiceHistory[]; total: number }> {
  let query = `page=${params.page}&pageSize=${params.pageSize}`
  if (params.category) 
query += `&category=${params.category}`
  if (params.status) 
query += `&status=${params.status}`
  if (params.invoiceType) 
query += `&invoiceType=${params.invoiceType}`
  const res = await get_(
    `${billApiPrefix}/api/v1/admin/invoice/histories?${query}`,
  )
  return res
}

export async function approveInvoice(id: string, payload: any) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以创建管理员促销活动
  const res = await post_(
    `${billApiPrefix}/api/v1/admin/invoice/histories/${id}/approve`,
    payload,
  )
  // 返回请求响应数据
  return res
}

export async function rejectInvoice(id: string, payload: any) {
  // 调用 post_ 函数发起 POST 请求，将 payload 数据发送到指定 API 以创建管理员促销活动
  const res = await post_(
    `${billApiPrefix}/api/v1/admin/invoice/histories/${id}/reject`,
    payload,
  )
  // 返回请求响应数据
  return res
}
// 获取负载明细
export async function getTaskPayloadList(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/usages/task-load/list?${params}`,
  )
  return res
}

// 获取存储资源统计趋势
export async function getStorageDurationReport(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/usages/overview/storage-duration?${params}`,
  )
  return res
}

/**
 * 获取存储总容量趋势图
 * @param payload
 * @returns 存储资源统计趋势
 */
export async function getStorageResourceTrend(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/usages/overview/storage-capacity?${params}`,
  )
  return res
}

/**
 * 获取ai资源总时长趋势统计
 * @param payload
 * @returns
 */
export async function getAiDurationReport(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/usages/overview/ai-duration?${params}`,
  )
  return res
}
/**
 * 获取ai卡时汇总
 * @param payload
 * @returns
 */
export async function getAiCardHourGroup(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(
    `${billApiPrefix}/api/v1/usages/overview/ai-card-hour-group?${params}`,
  )
  return res
}
/**
 * 获取消息列表
 * @param payload
 * @returns 消息列表
 */
export async function getMessageList(payload: any) {
  const params = Object.keys(payload || {})
    .filter(
      key =>
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== '',
    )
    .map(key => `${key}=${payload[key]}`)
    .join('&')
  const res = await get_(`/api/v1/notifications?${params}`)
  return res
}
